import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import {
  activityOutcomesAtom,
  openSnackbarAdviseAtom,
  reloadAtom,
  selectedBranchAtom,
  selectedCustomerIdAtom,
} from '../../../Atoms';
import {
  addMonths,
  isSameDay,
  isWeekend,
  nextMonday,
  set,
  startOfDay,
} from 'date-fns';
import { culture, getMonday, addZero, addDays } from '../../../Helpers/dates';
import capitalize from '../../../Helpers/StringsFormatting/capitalize';
import useAppointments from '../../../Pages/AppointmentsPage/hooks/useAppointments';
import EventDetailsDialog from '../../../Pages/AppointmentsPage/Components/EventDetailsDialog';
import AppointmentDialog from '../../../Components/Dialog/AppointmentDialog';
import { getEventStyle } from '../../../Pages/AppointmentsPage/Utils/constants';
import useTranslate from '../../../Hooks/useTranslate';
import {
  CenteredColumnFlexBox,
  CenteredFlexBox,
  ColumnFlexBox,
} from '../../FlexBox';
import MobileHeader from '../MobileHeader';
import SkeletonList from '../SkeletonList';

const SELECTED_DAY_GRADIENT = 'linear-gradient(160deg,#1D5A9D,#61B340)';

// Today, clamped to the current Mon–Fri work week (weekends fall back to Monday).
const initialSelectedDay = () => {
  const today = startOfDay(new Date());
  return isWeekend(today) ? startOfDay(getMonday()) : today;
};

const combineDateTime = (date, time) =>
  set(date, {
    hours: time.getHours(),
    minutes: time.getMinutes(),
    seconds: 0,
    milliseconds: 0,
  });

const weekdayLabel = (day) =>
  capitalize(
    day.toLocaleDateString(culture, { weekday: 'short' }).replace('.', '')
  );

/**
 * MobileAgenda — the Agenda tab. Mon–Fri day strip + timeline over
 * getEventsByperiod (reuses useAppointments), events tinted with the desktop
 * appointment/recall colors; details/edit open full-screen. See MOBILE_PLAN §F(8).
 */
const MobileAgenda = function MobileAgenda() {
  const theme = useTheme();
  const translate = useTranslate();
  const notify = useSetAtom(openSnackbarAdviseAtom);
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const selectedCustomerId = useAtomValue(selectedCustomerIdAtom);
  const activityOutcomes = useAtomValue(activityOutcomesAtom);
  const [reload, setReload] = useAtom(reloadAtom);

  const [weekStart, setWeekStart] = useState(() => startOfDay(getMonday()));
  const [selectedDay, setSelectedDay] = useState(initialSelectedDay);
  const [editDialog, setEditDialog] = useState(null);
  const [edit, setEdit] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [slideDir, setSlideDir] = useState('next');

  // Fetch the whole week (Mon → Sun) so Friday events are covered.
  const range = useMemo(() => [weekStart, addDays(weekStart, 7)], [weekStart]);
  // Work week: Monday to Friday.
  const weekDays = useMemo(
    () => Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const { events, loading, updateAppointment, endAppointment, refetch } =
    useAppointments({
      selectedBranch,
      range,
      enabled: !selectedCustomerId,
    });

  useEffect(() => {
    if (reload && !selectedCustomerId) {
      setReload(false);
      refetch();
    }
  }, [reload, selectedCustomerId, refetch, setReload]);

  const appointmentColors = useMemo(
    () =>
      theme.custom.generateConsistentUniqueColors(
        activityOutcomes?.APPOINTMENT?.map(({ name }) => name) || []
      ),
    [activityOutcomes, theme.custom]
  );
  const appointmentOutcomes = useMemo(
    () =>
      activityOutcomes?.APPOINTMENT?.map((outcome, index) => ({
        ...outcome,
        color: appointmentColors[index],
      })) || [],
    [activityOutcomes, appointmentColors]
  );

  const dayEvents = useMemo(
    () =>
      (events || [])
        .filter((event) => isSameDay(event.startDate, selectedDay))
        .sort((a, b) => a.startDate - b.startDate),
    [events, selectedDay]
  );

  // Swipe the week strip left/right to move a week (keeps the weekday selected).
  const goWeek = (delta) => {
    setSlideDir(delta < 0 ? 'prev' : 'next');
    setWeekStart((prev) => addDays(prev, delta * 7));
    setSelectedDay((prev) => addDays(prev, delta * 7));
  };

  // Chevrons jump by month, keeping the day-of-month (clamped to the last day).
  const goMonth = (delta) => {
    setSlideDir(delta < 0 ? 'prev' : 'next');
    const landed = startOfDay(addMonths(selectedDay, delta));
    // A weekend lands on the Monday after it.
    const finalDay = isWeekend(landed) ? nextMonday(landed) : landed;
    setSelectedDay(finalDay);
    setWeekStart(startOfDay(getMonday(finalDay)));
  };

  const touchStartX = useRef(null);
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) goWeek(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const handleEndAppointment = (payload, onSuccess) =>
    endAppointment(selectedAppointmentId, payload, {
      onSuccess: () => {
        setEditDialog(null);
        notify({
          message: translate('APPOINTMENT_COMPLETED'),
          severity: 'info',
        });
        if (onSuccess) onSuccess();
      },
      onError: () => notify({ message: translate('ERROR'), severity: 'error' }),
    });

  const handleSubmitEdit = (data) =>
    updateAppointment(
      selectedAppointmentId,
      {
        entityId: data.customer.id,
        scheduledOn: combineDateTime(data.date, data.time).toISOString(),
        notes: data.desc,
        assignee: data.assignee,
        remindBefore: data.remindBefore,
      },
      {
        onSuccess: () => {
          setEditDialog(null);
          setEdit(false);
          notify({
            message: translate('APPOINTMENT_EDITED'),
            severity: 'success',
          });
        },
        onError: () =>
          notify({ message: translate('EDIT_APP_ERROR'), severity: 'error' }),
      }
    );

  const openEvent = (event) => {
    setEditDialog(event);
    setSelectedAppointmentId(event?.appointmentId);
  };

  const monthNav = (
    <CenteredFlexBox sx={{ gap: 0.25 }}>
      <IconButton
        aria-label="previous month"
        size="small"
        onClick={() => goMonth(-1)}
        sx={{ color: 'greys.secondary' }}
      >
        <ChevronLeftRoundedIcon />
      </IconButton>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: 'primary.main',
          textTransform: 'capitalize',
        }}
      >
        {selectedDay.toLocaleDateString(culture, { month: 'long' })}
      </Typography>
      <IconButton
        aria-label="next month"
        size="small"
        onClick={() => goMonth(1)}
        sx={{ color: 'greys.secondary' }}
      >
        <ChevronRightRoundedIcon />
      </IconButton>
    </CenteredFlexBox>
  );

  const weekStrip = (
    <CenteredFlexBox
      key={weekStart.toISOString()}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      sx={{
        gap: 0.5,
        width: '100%',
        touchAction: 'pan-y',
        animation: `${slideDir === 'prev' ? 'dbeWeekPrev' : 'dbeWeekNext'} .28s ease`,
        '@keyframes dbeWeekNext': {
          from: { opacity: 0, transform: 'translateX(36px)' },
          to: { opacity: 1, transform: 'translateX(0)' },
        },
        '@keyframes dbeWeekPrev': {
          from: { opacity: 0, transform: 'translateX(-36px)' },
          to: { opacity: 1, transform: 'translateX(0)' },
        },
      }}
    >
      {weekDays.map((day) => {
        const selected = isSameDay(day, selectedDay);
        return (
          <Box
            key={day.toISOString()}
            role="button"
            onClick={() => setSelectedDay(day)}
            sx={{
              flex: 1,
              textAlign: 'center',
              py: 1,
              borderRadius: '14px',
              cursor: 'pointer',
              background: selected ? SELECTED_DAY_GRADIENT : 'transparent',
              boxShadow: selected ? '0 4px 12px rgba(29,91,157,.4)' : 'none',
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                color: selected ? 'rgba(255,255,255,.85)' : '#94a3b8',
              }}
            >
              {weekdayLabel(day)}
            </Typography>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: selected ? 700 : 600,
                mt: 0.5,
                color: selected ? '#fff' : '#3a3a42',
              }}
            >
              {day.getDate()}
            </Typography>
          </Box>
        );
      })}
    </CenteredFlexBox>
  );

  return (
    <>
      <MobileHeader
        title={translate('AGENDA')}
        avatar={monthNav}
        extra={weekStrip}
      />

      <ColumnFlexBox sx={{ p: 2, gap: 1.25 }}>
        <Typography
          sx={{ fontWeight: 600, textTransform: 'capitalize', mb: 0.5 }}
        >
          {selectedDay.toLocaleDateString(culture, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          })}
        </Typography>

        {loading ? (
          <SkeletonList count={4} height={72} gap={1.25} padding={0} />
        ) : dayEvents.length ? (
          dayEvents.map((event) => {
            const isAppointment = event.type === 'Appuntamento';
            const eventStyle = getEventStyle(
              event.type,
              event?.outcome === 17,
              theme
            ).style;
            return (
              <CenteredFlexBox key={event.appointmentId} sx={{ gap: 1.25 }}>
                <Typography
                  sx={{
                    width: 46,
                    flex: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    color: theme.custom.mobile.mutedColor,
                  }}
                >
                  {addZero(event.startDate)}
                </Typography>
                <Box
                  role="button"
                  onClick={() => openEvent(event)}
                  sx={{
                    ...eventStyle,
                    flex: 1,
                    minWidth: 0,
                    p: 1.25,
                    borderRadius: '12px',
                    cursor: 'pointer',
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                    {isAppointment
                      ? translate('APPOINTMENT_TYPE')
                      : translate('RECALL')}
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }} noWrap>
                    {event.title}
                  </Typography>
                  <Typography sx={{ fontSize: 12 }} noWrap>
                    {`${addZero(event.startDate)} – ${addZero(event.endDate)}${
                      event.desc ? ` · ${event.desc}` : ''
                    }`}
                  </Typography>
                </Box>
              </CenteredFlexBox>
            );
          })
        ) : (
          <CenteredColumnFlexBox
            sx={{
              minHeight: '35vh',
              gap: 1,
              color: theme.custom.mobile.mutedColor,
            }}
          >
            <CalendarMonthRoundedIcon sx={{ fontSize: 40 }} />
            <Typography variant="body2">
              {translate('MOBILE_NO_ITEMS')}
            </Typography>
          </CenteredColumnFlexBox>
        )}
      </ColumnFlexBox>

      <EventDetailsDialog
        open={Boolean(editDialog && !edit)}
        onClose={() => setEditDialog(null)}
        outcomes={appointmentOutcomes}
        handleEndAppointment={handleEndAppointment}
        setEdit={setEdit}
        event={editDialog}
        fullScreen
      />
      <AppointmentDialog
        title="CHANGE_EVENT"
        defaultData={editDialog}
        open={Boolean(editDialog && edit)}
        onClose={() => {
          setEditDialog(null);
          setEdit(false);
        }}
        selectedBranch={selectedBranch}
        onSubmit={handleSubmitEdit}
        fullScreen
      />
    </>
  );
};

export default MobileAgenda;
